import EventEmitter from "eventemitter3";
import gsap from "gsap";
import * as PIXI from "pixi.js";
import MyApplication from "../../../pixi/MyApplication";
import AssetsManager from "../../../pixi/AssetsManager";
import DataManager from "../../../data/DataManager";
import PandaView, { PANDA_LABEL } from "./PandaView";
import ReadyTextView from "./ReadyTextView";
import TutorialView from "./TutorialView";
import ScoreView from "./ScoreView";
import ObstacleView from "./ObstacleView";
import TrampolineView from "./TrampolineView";

export enum GAME_STATUS {
  READY,
  JET,
  HIT,
  OVER,
}

/**
 * GameViewクラス
 */
class GameView extends PIXI.Container {
  // ---------------------------------------------------------------------------------------------------------
  // --	VARS
  // ---------------------------------------------------------------------------------------------------------

  //イベントエミッター
  private _emitter: EventEmitter = new EventEmitter();
  public get emitter(): EventEmitter {
    return this._emitter;
  }

  //アプリケーション
  private _app: PIXI.Application;

  //コンテナ
  private _jumpContainer: PIXI.Container;
  private _container: PIXI.Container;
  private _mountainContainer: PIXI.Container;
  private _cloudContainer: PIXI.Container;
  private _landContainer: PIXI.Container;
  private _woodContainerR: PIXI.Container;
  private _woodContainerF: PIXI.Container;
  private _groundContainer: PIXI.Container;
  private _objContainer: PIXI.Container;

  //パンダ
  private _panda: PandaView;

  //READY
  private _readyText: ReadyTextView;

  //チュートリアル
  private _tutorial: TutorialView;

  //スコア
  private _scoreView: ScoreView;

  // ---------------------------------------------------------------------------------------------------------

  //速度
  private _speed: number = 0;
  private _speedLimit: number;
  private _speedY: number;

  //ステータス
  private _status: GAME_STATUS;
  public get status(): GAME_STATUS {
    return this._status;
  }

  //タッチスタートポイント
  private _touchID: number;
  private _touchStartPoint: PIXI.Point;

  //レーンインデックス
  private _laneIndex: number;

  //距離
  private _distance: number;

  //パンダ移動中フラグ
  private _pandaMovingFlag: boolean;

  //ターボフラグ
  private _turboFlag: boolean;

  //ジャンプ
  private _jumpFlag: boolean;
  private _fallFlag: boolean;

  //敵キャラ生成タイマー
  private _obstacleTimer: gsap.core.Tween;

  //レベルアップタイマー
  private _levelUpTimer: gsap.core.Tween;

  //トランポリン
  private _trampolineFlag: boolean;
  private _trampolineTimer: gsap.core.Tween;

  //スコア
  private _score: number;

  //ロックフラグ
  private _lockFlag: boolean;

  // ---------------------------------------------------------------------------------------------------------
  // --	GameView
  // ---------------------------------------------------------------------------------------------------------

  /**
   * コンストラクタ
   */
  constructor() {
    super();

    //app参照
    this._app = MyApplication.app;

    //ジャンプ用コンテナ
    this._jumpContainer = new PIXI.Container();
    this.addChild(this._jumpContainer);

    //コンテナ
    this._container = new PIXI.Container();
    this._jumpContainer.addChild(this._container);

    //山配置
    this._mountainContainer = new PIXI.Container();
    this._mountainContainer.x = -80;
    this._container.addChild(this._mountainContainer);
    for (let i = 0; i < 2; i++) {
      const mountain: PIXI.Sprite =
        AssetsManager.instance.createSpriteByName("mountain.png");
      mountain.scale.set(4);
      mountain.x = 2560 * i;
      mountain.y = 66 * 2 + 2;
      this._mountainContainer.addChild(mountain);
    }

    //雲配置
    this._cloudContainer = new PIXI.Container();
    this._container.addChild(this._cloudContainer);
    const cloudNum: number = 4;
    const cloudUnit: number = this._app.renderer.width / cloudNum;
    for (let i = 0; i < cloudNum; i++) {
      const cloud: PIXI.Sprite =
        AssetsManager.instance.createSpriteByName("cloud.png");
      cloud.scale.set(4);
      cloud.x = cloudUnit * i;
      cloud.y = Math.random() * 120;
      this._cloudContainer.addChild(cloud);
    }

    //地面配置
    this._landContainer = new PIXI.Container();
    this._container.addChild(this._landContainer);
    for (let i = 0; i < 4; i++) {
      const land: PIXI.Sprite =
        AssetsManager.instance.createSpriteByName("land.png");
      land.scale.set(4);
      land.x = (-216 + 216 * i) * 2;
      land.y = 210 * 2;
      this._landContainer.addChild(land);
    }

    //木（後ろ）配置
    this._woodContainerR = new PIXI.Container();
    this._container.addChild(this._woodContainerR);
    for (let i = 0; i < 12; i++) {
      const wood: PIXI.Sprite =
        AssetsManager.instance.createSpriteByName("woods.png");
      wood.scale.set(4);
      wood.x = (-70 + 70 * i) * 2;
      wood.y = 146 * 2 + 2;
      this._woodContainerR.addChild(wood);
    }

    //オブジェクトコンテナ
    this._objContainer = new PIXI.Container();
    this._objContainer.sortableChildren = true;
    this._container.addChild(this._objContainer);

    //パンダ配置
    this._panda = new PandaView();
    this._panda.gotoAndPlay(PANDA_LABEL.LABEL_STAND, false, 0);
    this._objContainer.addChild(this._panda);

    //グラウンド配置
    this._groundContainer = new PIXI.Container();
    this._container.addChild(this._groundContainer);
    for (let i = 0; i < 4; i++) {
      const ground: PIXI.Sprite =
        AssetsManager.instance.createSpriteByName("ground.png");
      ground.scale.set(4);
      ground.x = -640 + 640 * i;
      ground.y = 456 * 2;
      this._groundContainer.addChild(ground);
      const ground2: PIXI.Sprite =
        AssetsManager.instance.createSpriteByName("ground.png");
      ground2.scale.set(4);
      ground2.x = -640 + 640 * i;
      ground2.y = 456 * 2 + 56 * 4;
      this._groundContainer.addChild(ground2);
    }

    //木（前）配置
    this._woodContainerF = new PIXI.Container();
    this._container.addChild(this._woodContainerF);
    for (let i = 0; i < 12; i++) {
      const wood: PIXI.Sprite =
        AssetsManager.instance.createSpriteByName("kadan.png");
      wood.scale.set(4);
      wood.x = (-70 + 70 * i) * 2;
      wood.y = 418 * 2;
      this._woodContainerF.addChild(wood);
    }

    //レディーテキスト配置
    this._readyText = new ReadyTextView();
    this._container.addChild(this._readyText);

    //チュートリアル配置
    this._tutorial = new TutorialView();
    this._container.addChild(this._tutorial);

    //スコア配置
    this._scoreView = new ScoreView();
    this._scoreView.x = MyApplication.screenWidth;
    this._scoreView.y = 20;
    this.addChild(this._scoreView);

    //入力
    this._app.stage.on("pointerdown", this.onDragStart.bind(this));
    this._app.stage.on("pointermove", this.onDrag.bind(this));
    this._app.stage.on("pointerup", this.onDragEnd.bind(this));
    this._app.stage.on("pointerupoutside", this.onDragEnd.bind(this));

    //アップデート
    this._app.ticker.add(this.update.bind(this));
  }

  /**
   * リセット
   */
  reset() {
    //ゲームをリセット
    this.resetGame();

    //スコア非表示
    this._scoreView.visible = false;

    //位置を初期化
    this.y = this._app.screen.height - 280;
  }

  /**
   * ゲームをリセット
   */
  resetGame(): void {
    //パンダの初期化
    this._panda.gotoAndPlay(PANDA_LABEL.LABEL_STAND, false, 0);
    this._panda.x = -this._panda.width;
    this._panda.y = 660;
    this._panda.zIndex = this._panda.y;

    //障害物を削除
    let len: number = this._objContainer.children.length;
    for (let i: number = 0; i < len; i++) {
      //オブジェクト参照
      const obj: PIXI.DisplayObject = this._objContainer.getChildAt(
        i
      ) as PIXI.DisplayObject;

      if (obj instanceof ObstacleView) {
        //障害物を破棄
        const obstacle: ObstacleView = obj as ObstacleView;
        obstacle.destroy();

        //ループ用の調整
        len--;
        i--;
      }
    }

    //ターボフラグ初期化
    this._turboFlag = false;

    //ステータス初期化
    this._status = GAME_STATUS.READY;

    //タッチスタートポイントを破棄
    this._touchID = -1;
    this._touchStartPoint = null;

    //レーンインデックス
    this._laneIndex = 1;

    //距離と速度をリセット
    this._speed = 0;
    this._speedY = 0;
    this._distance = 0;

    //スコア表示更新
    this._score = 0;
    this.updateScore();

    //フラグ初期化
    this._pandaMovingFlag = false;

    //トランポリンフラグ初期化
    this._trampolineFlag = false;

    //ジャンプフラグ初期化
    this._jumpFlag = false;
    this._fallFlag = false;

    //ロックフラグ
    this._lockFlag = false;

    //最初のトランポリン配置
    const landIndex: number = Math.floor(Math.random() * 3);
    var obj: PIXI.DisplayObject = new TrampolineView(1);
    obj.x = MyApplication.screenWidth - 100;
    this._objContainer.addChild(obj);
  }

  // --------------------------------------------------------------------------------------------------------------------------------------
  // --	 開く/閉じる
  // --------------------------------------------------------------------------------------------------------------------------------------

  /**
   * 開く
   */
  open() {
    //アニメーション
    gsap.killTweensOf(this);
    gsap.to(this, {
      ease: "power2.inOut",
      duration: 60 / 60,
      y: 0,
      onComplete: this.opened.bind(this),
    });
  }

  /**
   * 開いた
   */
  opened(): void {
    //スコア表示
    this._scoreView.visible = true;

    //ゲームスタート
    gsap.delayedCall(0.5, this.start.bind(this));
  }

  /**
   * 閉じる
   */
  close() {
    //リセット
    this.reset();
  }

  // --------------------------------------------------------------------------------------------------------------------------------------
  // --	 GAME
  // --------------------------------------------------------------------------------------------------------------------------------------

  /**
   * スタート
   */
  start() {
    //パンダ登場
    this._panda.gotoAndPlay(PANDA_LABEL.LABEL_WALK, true, 8);
    gsap.to(this._panda, {
      ease: "none",
      duration: 60 / 60,
      x: 80,
      onComplete: this.ready.bind(this),
    });
  }

  /**
   * 準備
   */
  ready(): void {
    //SE
    AssetsManager.instance.playSound("sound_ready");

    //パンダたつ
    this._panda.gotoAndPlay(PANDA_LABEL.LABEL_STAND, false, 0);

    //READY表示
    this._readyText.open();

    //チュートリアル表示
    this._tutorial.show();

    //入力受付
    this._app.stage.interactive = true;
    this._app.stage.cursor = "pointer";
  }

  /**
   * GO!!
   */
  go(): void {
    //BGM
    AssetsManager.instance.playSound("sound_bgm", { loop: true, volume: 0.1 });

    //速度の初期化
    const config: any = DataManager.instance.getData("config");
    this._speedLimit = config.SPEED_LIMIT_START;

    //ステータス変更
    this._status = GAME_STATUS.JET;

    //チュートリアル非表示
    this._readyText.close();
    this._tutorial.reset();

    //パンダジェット
    this._panda.gotoAndPlay(PANDA_LABEL.LABEL_JET, true, 10);
  }

  /**
   * 障害物生成
   */
  private createObstacle(): void {
    //障害物生成
    if (!this._lockFlag) {
      const landIndex: number = Math.floor(Math.random() * 3);
      var obstacle: PIXI.DisplayObject = this._trampolineFlag
        ? new TrampolineView(landIndex)
        : new ObstacleView(landIndex);
      this._objContainer.addChild(obstacle);
    }

    //トランポリンフラグを戻す
    if (this._trampolineFlag) {
      this._trampolineFlag = false;

      //次のトランポリン
      const config: any = DataManager.instance.getData("config");
      this._levelUpTimer = gsap.delayedCall(config.TRAMPOLINE_TIME, () => {
        this._trampolineFlag = true;
      });
    }

    //次の障害物生成
    const config: any = DataManager.instance.getData("config");
    this._obstacleTimer = gsap.delayedCall(
      (config.OBSTACLE_TIME + Math.random() * config.OBSTACLE_RND) / 60,
      this.createObstacle.bind(this)
    );
  }

  /**
   * ターボ
   */
  private turbo(): void {
    //config
    const config: any = DataManager.instance.getData("config");

    //フラグ立てる
    this._turboFlag = true;

    //スピードアップ
    this._speedLimit = config.SPEED_LIMIT;
    this._panda.powerUp();

    //障害物生成
    this._obstacleTimer = gsap.delayedCall(
      30 / 60,
      this.createObstacle.bind(this)
    );

    //レベルアップ
    this._levelUpTimer = gsap.delayedCall(
      config.LEVEL_UP_TIME,
      this.levelUP.bind(this)
    );

    //トランポリン
    this._levelUpTimer = gsap.delayedCall(config.TRAMPOLINE_TIME, () => {
      this._trampolineFlag = true;
    });
  }

  /**
   * レベルアップ
   */
  private levelUP(): void {
    //スピードアップ
    this._speedLimit += 1;

    //次のレベルアップ
    const config: any = DataManager.instance.getData("config");
    this._levelUpTimer = gsap.delayedCall(
      config.LEVEL_UP_TIME,
      this.levelUP.bind(this)
    );
  }

  /**
   * ジャンプ
   */
  jump(): void {
    //SE
    AssetsManager.instance.playSound("sound_jump");

    //フラグを立てる
    this._jumpFlag = true;
    this._fallFlag = false;

    //速度
    this._speedY = -24 - 4 * Math.random();
  }

  /**
   * 衝突
   */
  hit(): void {
    //BGM止める
    AssetsManager.instance.stopSound("sound_bgm");

    //ミスのSE
    AssetsManager.instance.playSound("sound_hit");

    //ミスイベント発行（演出）
    this._emitter.emit("MISS");

    //ステータス変更
    this._status = GAME_STATUS.HIT;

    //入力停止
    this._app.stage.interactive = false;
    this._app.stage.cursor = "";

    //レベルアップ停止
    if (this._levelUpTimer) {
      this._levelUpTimer.kill();
      this._levelUpTimer = null;
    }

    //障害物生成停止
    if (this._obstacleTimer) {
      this._obstacleTimer.kill();
      this._obstacleTimer = null;
    }

    //トランポリン停止
    if (this._trampolineTimer) {
      this._trampolineTimer.kill();
      this._trampolineTimer = null;
    }

    //パンダ
    gsap.killTweensOf(this._panda);
    this._panda.gotoAndPlay(PANDA_LABEL.LABEL_HIT, true, 8);

    //速度反転
    this._speed = -5;
    this._speedY = -16;
  }

  /**
   * 着地
   */
  private land(): void {
    //SE
    AssetsManager.instance.playSound("sound_voice");

    //ステータス変更
    this._status = GAME_STATUS.OVER;

    //速度初期化
    this._speedY = 0;
    this._speed = 0;
    this._panda.panda.y = 0;

    //間を置いてゲームオーバー
    gsap.delayedCall(0.5, this.gameOver.bind(this));
  }

  /**
   * ミス
   */
  gameOver() {
    //オーバーイベント発行
    gsap.delayedCall(1.5, () => {
      // AssetsManager.instance.playSound("sound_over");
      this._emitter.emit("OVER", this._score);
    });
  }

  // --------------------------------------------------------------------------------------------------------------------------------------
  // --	 入力
  // --------------------------------------------------------------------------------------------------------------------------------------

  /**
   * ドラッグ開始
   */
  onDragStart(e) {
    //タッチID保持
    if (this._touchID != -1) return;
    this._touchID = e.data.identifier;

    //GO
    if (this._status == GAME_STATUS.READY) this.go();

    //タッチスタート位置を保存
    this._touchStartPoint = new PIXI.Point(e.data.global.x, e.data.global.y);
  }

  /**
   * ドラッグ
   */
  onDrag(e) {
    //IDが違う場合は無視
    if (this._touchID != e.data.identifier) return;

    if (this._touchStartPoint) {
      let vecY: number = e.data.global.y - this._touchStartPoint.y;
      if (vecY > 40) {
        this.swipe(1);
      } else if (vecY < -40) {
        this.swipe(-1);
      }
    } else {
      if (!this._pandaMovingFlag) {
        this._touchStartPoint = new PIXI.Point(
          e.data.global.x,
          e.data.global.y
        );
      }
    }
  }

  /**
   * ドラッグ終了
   */
  onDragEnd(e) {
    //IDリセット
    if (this._touchID != e.data.identifier) return;
    this._touchID = -1;

    //スタートポイントを破棄
    this._touchStartPoint = null;
  }

  /**
   * スワイプ
   */
  swipe(vec: number): void {
    //ジャンプ中は無視
    if (this._jumpFlag) return;

    //タッチスタートポイントを破棄
    this._touchStartPoint = null;

    //レーンインデックス変更
    this._laneIndex += vec;
    if (this._laneIndex < 0) {
      this._laneIndex = 0;
      return;
    } else if (this._laneIndex > 2) {
      this._laneIndex = 2;
      return;
    }

    //SE
    AssetsManager.instance.playSound("sound_move");

    //フラグを立てる
    this._pandaMovingFlag = true;

    //パンダ移動
    gsap.killTweensOf(this._panda);
    gsap.to(this._panda, {
      ease: "none",
      duration: 5 / 60,
      y: 520 + 140 * this._laneIndex,
      onUpdate: () => {
        this._panda.zIndex = this._panda.y;
      },
      onComplete: () => {
        this._pandaMovingFlag = false;
      },
    });
  }

  // --------------------------------------------------------------------------------------------------------------------------------------
  // --	 アップデート
  // --------------------------------------------------------------------------------------------------------------------------------------

  update(delta: number) {
    //スクロール
    this.scroll(delta);

    //表示更新
    this.updateBg(delta);

    //雲アップデート
    this.updateClouds(delta);

    //障害物アップデート
    this.updateObstacle(delta);

    //得点更新
    if (this._status == GAME_STATUS.JET) this.updateScore();
  }

  /**
   * スクロール
   */
  private scroll(delta: number): void {
    switch (this._status) {
      case GAME_STATUS.READY:
        break;
      case GAME_STATUS.JET:
        this.jetEn(delta);
        break;
      case GAME_STATUS.HIT:
        this.hitEn(delta);
        break;
    }

    //移動
    this._distance += this._speed * delta;
  }

  /**
   * ジェット時のエンジン
   */
  private jetEn(delta: number): void {
    //横移動
    const jumpAccell: number = this._jumpFlag ? 1.5 : 1;
    this._speed += (this._speedLimit * jumpAccell - this._speed) / 3;

    //縦移動
    this._speedY += 0.3 * delta;
    this._panda.panda.y += this._speedY * delta;

    //落下時のSE
    if (this._jumpFlag && !this._fallFlag && this._speedY > 0) {
      this._fallFlag = true;
      AssetsManager.instance.playSound("sound_fall");
    }

    //着地前に障害物の生成をロック
    if (
      this._panda.panda.y > -600 &&
      !this._lockFlag &&
      this._jumpFlag &&
      this._speedY > 0
    ) {
      this._lockFlag = true;
    }

    //接地
    if (this._panda.panda.y >= 0) {
      this._panda.panda.y = 0;
      this._speedY *= -0.2;

      //SE
      if (this._jumpFlag) {
        this._jumpFlag = false;
        AssetsManager.instance.playSound("sound_land");
      }

      //着地
      if (Math.abs(this._speedY) <= 0.3 * delta) {
        this._lockFlag = false;
        this._speedY = 0;
      }
    }

    //縦スクロール
    if (this._panda.panda.y < -300) {
      this._jumpContainer.y = -(this._panda.panda.y + 300);
    } else {
      this._jumpContainer.y = 0;
    }
  }

  /**
   * ヒット時のエンジン
   */
  private hitEn(delta: number): void {
    //重力
    this._speedY += 1 * delta;

    //移動
    this._panda.panda.y += this._speedY * delta;

    //接地
    if (this._panda.panda.y >= 0) {
      this._panda.panda.y = 0;
      this._speedY *= -0.8;
      this._speed *= 0.5;

      //着地
      if (Math.abs(this._speedY) <= 0.5 * delta) {
        this.land();
      }
    }
  }

  /**
   * 背景アップデート
   */
  private updateBg(delta: number): void {
    this._woodContainerR.x = -this._distance % 140;
    this._woodContainerF.x = -this._distance % 140;
    this._landContainer.x = -this._distance % 432;
    this._groundContainer.x = -this._distance % 640;
    this._mountainContainer.x = -(this._distance / 8) % 2560;
  }

  /**
   * 障害物アップデート
   */
  private updateObstacle(delta: number): void {
    //配列初期化
    var destroyArray: Array<PIXI.DisplayObject> =
      new Array<PIXI.DisplayObject>();

    //オブジェクトごとの処理
    let len: number = this._objContainer.children.length;
    for (let i: number = 0; i < len; i++) {
      //オブジェクト参照
      const obj: PIXI.DisplayObject = this._objContainer.getChildAt(i);

      //障害物
      if (obj instanceof ObstacleView) {
        //障害物をアップデート
        const obstacle: ObstacleView = obj as ObstacleView;
        obstacle.update(this._speed * delta);

        //画面外は破棄配列に格納
        if (obstacle.x < -obstacle.width) {
          destroyArray.push(obstacle);
        }
        //ヒットテスト
        else if (this._status == GAME_STATUS.JET && !this._jumpFlag) {
          if (obstacle.hitTestPanda(this._panda)) this.hit();
        }
      }
      //トランポリン
      else if (obj instanceof TrampolineView) {
        //トランポリンをアップデート
        const trampoline: TrampolineView = obj as TrampolineView;
        trampoline.update(this._speed * delta);

        //ターボ
        if (!this._turboFlag && trampoline.x < this._panda.x) this.turbo();

        //画面外は破棄配列に格納
        if (trampoline.x < -trampoline.width) {
          destroyArray.push(trampoline);
        }
        //ヒットテスト
        else if (this._status == GAME_STATUS.JET && !this._jumpFlag) {
          if (trampoline.hitTestPanda(this._panda)) this.jump();
        }
      }
    }

    //破棄
    len = destroyArray.length;
    for (let i = 0; i < len; i++) {
      const obj: PIXI.DisplayObject = destroyArray[i];
      obj.destroy();
    }
  }

  /**
   * 雲アップデート
   */
  private updateClouds(delta: number): void {
    let len: number = this._cloudContainer.children.length;
    for (let i = 0; i < len; i++) {
      //雲参照
      const cloud: PIXI.Sprite = this._cloudContainer.getChildAt(
        i
      ) as PIXI.Sprite;

      //移動
      cloud.x -= (0.4 + this._speed / 8) * delta;

      //ループ
      if (cloud.x < -cloud.width) {
        cloud.x = MyApplication.screenWidth;
        cloud.y = Math.random() * 120;
      }
    }
  }

  /**
   * スコアのアップデート
   */
  private updateScore(): void {
    this._score = Math.round(this._distance / 100);
    this._scoreView.update(this._score);
  }

  // --------------------------------------------------------------------------------------------------------------------------------------
  // --	 リサイズ
  // --------------------------------------------------------------------------------------------------------------------------------------

  resize() {
    //コンテナの位置
    const config: any = DataManager.instance.getData("config");
    this._container.y = (this._app.renderer.height - config.STAGE_HEIGHT) / 2;

    //チュートリアルの位置
    this._tutorial.x = MyApplication.screenWidth / 2 - 32;
    this._tutorial.y = MyApplication.screenHeight / 2 + 200;

    //レディーテキストの位置
    this._readyText.y =
      MyApplication.screenHeight / 2 -
      config.STAGE_HEIGHT / 2 +
      this._readyText.height / 2 +
      100 -
      this._container.y * 2;
  }

  // ---------------------------------------------------------------------------------------------------------
}

export default GameView;
